import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { apiClient, type PaginatedResult } from './api-client';

/**
 * Centralised SEO (SEO master prompt §29).
 *
 * One table behind one editor: a product, a category, a CMS page and the
 * homepage are all rows of the same shape here, which is why the records
 * screen can filter "everything missing a description" in a single query
 * rather than a union over five entity tables.
 */

export type SeoEntityType =
  | 'HOME'
  | 'ROUTE'
  | 'PAGE'
  | 'PRODUCT'
  | 'CATEGORY'
  | 'BRAND'
  | 'BLOG'
  | 'LANDING_PAGE'
  | 'LOCATION';

export type SeoSchemaType =
  | 'NONE'
  | 'WEBSITE'
  | 'ORGANIZATION'
  | 'PRODUCT'
  | 'ARTICLE'
  | 'BREADCRUMB_LIST'
  | 'FAQ_PAGE'
  | 'ITEM_LIST'
  | 'LOCAL_BUSINESS'
  | 'COLLECTION_PAGE';

export type SeoFilter =
  | 'ALL'
  | 'MISSING_TITLE'
  | 'MISSING_DESCRIPTION'
  | 'MISSING_OG_IMAGE'
  | 'NOINDEX'
  | 'DUPLICATE_TITLE';

export interface SeoRecord {
  id: string;
  entityType: SeoEntityType;
  entityId: string;
  metaTitle: string | null;
  metaDescription: string | null;
  metaKeywords: string | null;
  focusKeyword: string | null;
  secondaryKeywords: string | null;
  canonicalUrl: string | null;
  robotsIndex: boolean;
  robotsFollow: boolean;
  ogTitle: string | null;
  ogDescription: string | null;
  ogImageUrl: string | null;
  ogType: string | null;
  twitterCard: 'SUMMARY' | 'SUMMARY_LARGE_IMAGE';
  twitterTitle: string | null;
  twitterDescription: string | null;
  twitterImageUrl: string | null;
  schemaType: SeoSchemaType;
  schemaJson: string | null;
  updatedByName: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface SeoDashboard {
  configuredByType: Array<{ entityType: string; count: number }>;
  totalConfigured: number;
  coverage: Array<{ entityType: string; total: number; configured: number }>;
  noIndexCount: number;
  missingTitle: number;
  missingDescription: number;
  missingOgImage: number;
  missingFocusKeyword: number;
  duplicateTitles: Array<{ title: string; count: number }>;
  duplicateDescriptions: Array<{ description: string; count: number }>;
  redirects: { total: number; active: number; unused: number };
}

export interface SeoRedirect {
  id: string;
  fromPath: string;
  toPath: string;
  status: 'PERMANENT' | 'TEMPORARY';
  isActive: boolean;
  hitCount: number;
  lastHitAt: string | null;
  notes: string | null;
  createdAt: string;
  updatedAt: string;
}

const keys = {
  all: ['seo'] as const,
  dashboard: () => ['seo', 'dashboard'] as const,
  records: (query?: unknown) => ['seo', 'records', query] as const,
  entity: (type: string, id: string) => ['seo', 'entity', type, id] as const,
  redirects: (query?: unknown) => ['seo', 'redirects', query] as const,
};

export function useSeoDashboard() {
  return useQuery({
    queryKey: keys.dashboard(),
    queryFn: () => apiClient.get<SeoDashboard>('/admin/seo/dashboard'),
  });
}

export interface SeoRecordQuery {
  entityType?: SeoEntityType | '';
  search?: string;
  filter?: SeoFilter;
  page?: number;
  limit?: number;
}

export function useSeoRecords(query: SeoRecordQuery) {
  const params = new URLSearchParams();
  if (query.entityType) params.set('entityType', query.entityType);
  if (query.search) params.set('search', query.search);
  if (query.filter && query.filter !== 'ALL') params.set('filter', query.filter);
  params.set('page', String(query.page ?? 1));
  params.set('limit', String(query.limit ?? 20));

  return useQuery({
    queryKey: keys.records(params.toString()),
    queryFn: () => apiClient.get<PaginatedResult<SeoRecord>>(`/admin/seo?${params.toString()}`),
  });
}

/** `null` when nobody has written SEO for this entity yet — not an error. */
export function useSeoForEntity(entityType: SeoEntityType | null, entityId: string | null) {
  return useQuery({
    queryKey: keys.entity(entityType ?? '', entityId ?? ''),
    queryFn: () =>
      apiClient.get<SeoRecord | null>(`/admin/seo/entity/${entityType}/${entityId}`),
    enabled: entityType !== null && entityId !== null,
  });
}

export function useSaveSeo() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (body: Record<string, unknown>) => apiClient.post<SeoRecord>('/admin/seo', body),
    // The dashboard's counts and the records list both move on every save, so
    // the whole namespace is invalidated rather than three keys by hand.
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: keys.all }),
  });
}

export function useDeleteSeo() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => apiClient.delete<void>(`/admin/seo/${id}`),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: keys.all }),
  });
}

export function useRedirects(search: string) {
  const params = new URLSearchParams();
  if (search) params.set('search', search);
  params.set('limit', '100');

  return useQuery({
    queryKey: keys.redirects(params.toString()),
    queryFn: () =>
      apiClient.get<PaginatedResult<SeoRedirect>>(`/admin/seo/redirects?${params.toString()}`),
  });
}

export function useSaveRedirect() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, body }: { id: string | null; body: Record<string, unknown> }) =>
      id
        ? apiClient.patch<SeoRedirect>(`/admin/seo/redirects/${id}`, body)
        : apiClient.post<SeoRedirect>('/admin/seo/redirects', body),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: keys.all }),
  });
}

export function useDeleteRedirect() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => apiClient.delete<void>(`/admin/seo/redirects/${id}`),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: keys.all }),
  });
}

/** Human labels. The enum values are shouty and appear all over the screens. */
export const ENTITY_LABELS: Record<SeoEntityType, string> = {
  HOME: 'Homepage',
  ROUTE: 'Site route',
  PAGE: 'CMS page',
  PRODUCT: 'Product',
  CATEGORY: 'Category',
  BRAND: 'Brand',
  BLOG: 'Blog post',
  LANDING_PAGE: 'Landing page',
  LOCATION: 'Location',
};
